import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Accounts, Permissions, User, Workspace } from '@local/client-contracts';
import { KeyName, isEnterKey } from '@local/ts-infra';
import { FilterInvoker, PopupRef, PopupService, UAutoCompleteComponent, UDropdownComponent, INIT_POSITION_POPUP } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventsService } from '@shared/services';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { SessionService } from '@shared/services/session.service';
import { randomColor, windowSizeObserver } from '@shared/utils';
import { getDefaultProfile } from '@shared/utils/set-icon.util';
import { cloneDeep, isEqual } from 'lodash';
import { OverlayPanel } from 'primeng/overlaypanel';
import { take } from 'rxjs';
import { WorkspaceMemberDetails } from '../../services/avatar-list.service';
import { HubService } from '../../services/hub.service';
import { IdentitiesSuggestionsService, SuggestionsList } from '../../services/identities-suggestions.service';
import { OptionsPopupComponent } from '../../views/hub/components/launcher-avatar-popup/options-popup.component';
import { AppPopupComponent, AppPopupData } from '../app-popup/app-popup.component';
import {
  DEFAULT_SCOPE_PERMISSION_OPTIONS,
  OwOrAdminsSettings,
  ScopePermissionsOptions,
  ShareOptionsPermissionsModel,
  ViewModelInvited,
  WorkspaceMember,
  WorkspacePermission,
} from '../share-options-permissions/share-options-permissions.component';
import {
  VerificationOptionsDetailsModel,
  ViewModelVerifier,
} from '../verifications/verification-options-details/verification-options-details.component';

type SuggestionsModel = ViewModelInvited[] | ViewModelVerifier[];

@UntilDestroy()
@Component({
  selector: 'members-selector',
  templateUrl: './members-selector.component.html',
  styleUrls: ['./members-selector.component.scss'],
})
export class MembersSelectorComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  private readonly HEIGHT_HEADER_POPUP = 175;
  readonly WS_MESSAGE = 'Everyone in this workspace can view';
  readonly accessTooltip = 'This person can still edit\n because the workspace in which\n it is located has editing permissions';
  readonly warningTooltip = "If a member had no previous permissions, appointing\n them as a verifier grants them editor's permissions";
  readonly externalTooltip = 'An external group from\n the Slack channel';
  readonly MEMBER_ICON_FALLBACK = getDefaultProfile();
  readonly ADMINS_TOOLTIP_LIMIT = 10;
  scopePermissionOptions: ScopePermissionsOptions[] = DEFAULT_SCOPE_PERMISSION_OPTIONS;
  private windowSize$ = windowSizeObserver();
  private keyHandlerId: string;
  private screenHeight: number;
  private isSelectedSuggestions: boolean;
  private scopePermissionsAutoComplete: Permissions.ShareScope = 'read';
  private _workspacePermission: WorkspacePermission;
  private _invitedSuggestions: SuggestionsModel;
  private _model: ShareOptionsPermissionsModel | VerificationOptionsDetailsModel;
  private indexDropdownOpened: number;
  currentUserId: string;
  filterTags: FilterInvoker;

  @Input() set model(val: ShareOptionsPermissionsModel | VerificationOptionsDetailsModel) {
    this._model = val;
    if (this.model.scopePermissionOptions?.length) {
      this.scopePermissionOptions = this.model.scopePermissionOptions;
    }
    if (this.model && (this.model as ShareOptionsPermissionsModel).defaultScopePermission) {
      this.scopePermissionsAutoComplete = (this.model as ShareOptionsPermissionsModel).defaultScopePermission;
    }
  }
  @Input() viewType: 'share' | 'verification';
  @Input() selectedSuggestions: WorkspaceMember[] = [];
  @Input() suggestions: SuggestionsList = [];
  @Input() creator: Accounts.WorkspaceAccount | User.Info;
  @Input() owOrAdmins: OwOrAdminsSettings;
  @Input() workspace: Workspace.Workspace;
  @Input() heightPopup: number;
  @Input() heightScroll: number;
  @Input() disabled: boolean;
  @Input() showShareButton = true;
  @Input() isEditor = true;

  get model() {
    return this._model;
  }

  get workspacePermission(): WorkspacePermission {
    return this._workspacePermission;
  }

  @Input()
  set workspacePermission(value: WorkspacePermission) {
    if (isEqual(this._workspacePermission, value)) {
      return;
    }
    this._workspacePermission = cloneDeep(value);
  }

  get invitedSuggestions(): SuggestionsModel {
    return this._invitedSuggestions;
  }

  @Input()
  set invitedSuggestions(value: SuggestionsModel) {
    if (isEqual(this._invitedSuggestions, value)) {
      return;
    }
    this._invitedSuggestions = cloneDeep(value);
  }

  @Output() creatorIsVerifier = new EventEmitter<boolean>();
  @Output() inviteChanged = new EventEmitter<SuggestionsModel>();
  @Output() selectChanged = new EventEmitter<ViewModelVerifier>();
  @Output() permissionChanged = new EventEmitter<WorkspacePermission>();

  @ViewChild('autoComplete') autoCompleteRef: UAutoCompleteComponent;
  @ViewChildren(UDropdownComponent) dropdownList: UDropdownComponent[];

  get isMaxHightPopup(): boolean {
    if (this.invitedSuggestions.length + (this.owOrAdmins?.count ? 1 : 0) > 3) return true;
    return this.heightPopup > this.screenHeight;
  }

  get heightScrollPopup(): string {
    if (this.heightPopup > this.screenHeight) {
      return `${this.screenHeight - this.HEIGHT_HEADER_POPUP}px`;
    }
    return `${this.heightScroll}px`;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private keyboardService: KeyboardService,
    private sessionService: SessionService,
    private eventsService: EventsService,
    private hubService: HubService,
    private popupService: PopupService,
    @Optional() private ref: PopupRef<OptionsPopupComponent, ShareOptionsPermissionsModel>,
    private identitiesSuggestionsService: IdentitiesSuggestionsService
  ) {
    this.filterTags = (event): SuggestionsList => this.identitiesSuggestionsService.searchSuggestions(event.query, this.suggestions);
  }

  ngOnInit(): void {
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => this.handleKeys(keys, event), 9);
    this.cdr.markForCheck();
    this.windowSize$.pipe(untilDestroyed(this)).subscribe((value) => {
      this.screenHeight = value.height;
      this.cdr.markForCheck();
    });
    this.sessionService.current$.pipe(untilDestroyed(this)).subscribe((s) => (this.currentUserId = s?.user.id));
  }

  ngAfterViewInit(): void {
    this.focusInput();
  }

  ngAfterViewChecked(): void {
    if (this.viewType === 'share') {
      this.focusInput();
    }
  }

  ngOnDestroy(): void {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    }
  }

  private focusInput() {
    setTimeout(() => {
      this.autoCompleteRef?.autoComplete?.inputEL.nativeElement.focus();
    }, 10);
  }

  private blurInput() {
    this.autoCompleteRef?.autoComplete?.el?.nativeElement?.children[0]?.children[0]?.lastChild?.firstChild?.blur();
  }

  private convertToScopePermissions(event: ScopePermissionsOptions): Permissions.ShareScope {
    return event.value;
  }

  private convertToScopePermissionsOptions(scopePermissions: Permissions.ShareScope): ScopePermissionsOptions {
    return this.scopePermissionOptions.find((s) => s.value === scopePermissions);
  }

  convertToDisplayScope(scopePermissions: string): string {
    if (['read', 'write'].includes(scopePermissions)) {
      return this.scopePermissionOptions.find((s) => s.value === scopePermissions)?.label;
    }
    return scopePermissions;
  }

  getSelectedOption(autoComplete: boolean, workspace: boolean, indexInvited: number): ScopePermissionsOptions {
    let scope;
    if (autoComplete) {
      scope = this.scopePermissionsAutoComplete;
    }
    if (workspace) {
      scope = this.workspacePermission.scope;
    }
    if (indexInvited !== undefined && indexInvited > -1) {
      scope = this.invitedSuggestions[indexInvited]?.scopePermissions;
    }
    return this.convertToScopePermissionsOptions(scope);
  }

  onSelectedSuggestions(event: WorkspaceMember[]): void {
    if (event.length > this.selectedSuggestions?.length) {
      const selected = event[event.length - 1];
      this.sendImpressionEvent(`select_${selected?.type === 'account' ? 'member' : 'group'}`);

      if (this.viewType === 'verification') {
        const newVerifier = {
          invited: selected as WorkspaceMemberDetails,
          type: selected.type,
          backgroundColor: randomColor(),
        } as ViewModelVerifier;
        this.selectChanged.emit(newVerifier);
        this.selectedSuggestions = [];
        this.disableSelectedItemInSuggestions(selected);
        this.cdr.markForCheck();
        return;
      }
    } else {
      const removeUser = this.selectedSuggestions[this.selectedSuggestions.length - 1];
      this.sendImpressionEvent(`remove_${removeUser.type === 'account' ? 'member' : 'group'}`);
    }
    this.selectedSuggestions = event || [];
    this.isSelectedSuggestions = true;
    this.cdr.markForCheck();
  }

  onShowDropdown(selectedIndex: number) {
    this.sendImpressionEvent('open_menu', 'permission_option');
    this.indexDropdownOpened = selectedIndex;
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event) {
    if (event.target?.closest('.p-overlay') || event.target?.closest(`.dropdown-${this.indexDropdownOpened}`)) {
      return;
    }
    this.closeDropdowns();
  }

  closeDropdowns() {
    this.indexDropdownOpened = null;
    this.dropdownList?.forEach((element) => {
      element?.dropdown?.hide();
    });
  }

  onClickInvite(event: CustomKeyboardEvent | MouseEvent): void {
    event.stopPropagation();
    if (!this.selectedSuggestions.length) return;
    this.sendImpressionEvent('share');

    this.invitedSuggestions.push(
      // eslint-disable-next-line no-unsafe-optional-chaining
      ...this.selectedSuggestions?.map((s) => {
        this.disableSelectedItemInSuggestions(s);
        return {
          invited: s,
          scopePermissions: this.scopePermissionsAutoComplete,
          type: s.type,
          backgroundColor: ['group', 'external-group'].includes(s.type) ? randomColor() : null,
        } as ViewModelInvited;
      })
    );
    this.inviteChanged.emit(this.invitedSuggestions);
    this.selectedSuggestions = [];
    this.cdr.markForCheck();
  }

  disableSelectedItemInSuggestions(selected: WorkspaceMember) {
    this.suggestions.forEach((s) => {
      if (
        (s.name === 'Members' && selected.type === 'account') ||
        (s.name === 'Groups' && ['group', 'external-group'].includes(selected.type))
      ) {
        s.items = s.items.map((item) => {
          if (item.id === selected.id) {
            return { ...item, disable: true };
          }
          return item;
        });
      }
    });
  }

  openRemoveVerifierPopup() {
    const replaceVerifierPopupRef = this.popupService.open<AppPopupComponent, AppPopupData>(
      'center',
      AppPopupComponent,
      {
        message: 'To remove the assigned verifier,<br> please replace them with<br> another verifier',
        showButtons: true,
        onlyPrimaryButton: true,
        rightButtonStyle: { type: 'primary', size: 89 },
        content: {
          primaryButton: 'Got it',
        },
        messageStyle: { fontSize: '14px' },
      },
      { position: INIT_POSITION_POPUP }
    );
    replaceVerifierPopupRef.compInstance.primaryButton.pipe(take(1)).subscribe(() => {
      this.focusInput();
    });
  }

  onDeleteInvited(indexInvited): void {
    this.closeDropdowns();
    if (this.disabled) {
      return;
    }
    if (this.viewType === 'verification') {
      if (this.invitedSuggestions.length === 1) {
        this.openRemoveVerifierPopup();
        return;
      }
    }
    if (indexInvited === undefined || indexInvited === -1) return;

    const invited = this.invitedSuggestions[indexInvited];
    this.suggestions.forEach((s) => {
      if (
        (s.name === 'Members' && invited.type === 'account') ||
        (s.name === 'Groups' && ['group', 'external-group'].includes(invited.type))
      ) {
        s.items = s.items.map((item) => {
          if (item.id === invited.invited?.id) {
            return { ...item, disable: false };
          }
          return item;
        });
      }
    });
    this.sendImpressionEvent(`remove_${invited.type === 'account' ? 'member' : 'group'}`);
    this.invitedSuggestions.splice(indexInvited, 1);
    this.inviteChanged.emit(this.invitedSuggestions);
    this.cdr.markForCheck();
  }

  onToggleButtonChange(): void {
    this.sendImpressionEvent(this.workspacePermission.shared ? 'remove_workspace' : 'workspace');
    this.workspacePermission.shared = !this.workspacePermission.shared;
    this.permissionChanged.emit(this.workspacePermission);
    this.cdr.markForCheck();
  }

  onChangeScopePermissions(event: ScopePermissionsOptions, autoComplete: boolean, workspace: boolean, indexInvited: number): void {
    this.sendImpressionEvent(event.label.toLowerCase(), 'permission_option');
    this.closeDropdowns();
    if (autoComplete) {
      this.scopePermissionsAutoComplete = this.convertToScopePermissions(event);
    }
    if (workspace) {
      this.workspacePermission.scope = this.convertToScopePermissions(event);
      this.permissionChanged.emit(this.workspacePermission);
    }
    if (indexInvited !== undefined && indexInvited > -1) {
      this.invitedSuggestions[indexInvited].scopePermissions = this.convertToScopePermissions(event);
      this.inviteChanged.emit(this.invitedSuggestions);
    }
    this.cdr.markForCheck();
  }

  sendImpressionEvent(target: string, label?: string): void {
    this.eventsService.event('home_tabs.share', {
      name: this.model.telemetryName,
      location: { title: this.hubService.currentLocation },
      target,
      label: label || this.viewType === 'share' ? 'share_option' : 'verification_option',
    });
  }

  onHidePanel(op: OverlayPanel) {
    op.overlayVisible = false;
    op.cd.markForCheck();
  }

  private handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    const key = keys[0];
    if (isEnterKey(key) || key === 'space') {
      event.stopImmediatePropagation();
      if (this.isSelectedSuggestions) {
        this.isSelectedSuggestions = false;
        return;
      }
      if (this.selectedSuggestions.length) {
        this.onClickInvite(event);
        return;
      }
    }
  }
}
